export const FUSIONNOW_BASE_URL =
  process.env.REACT_APP_FUSIONNOW_BASE_URL ||
  'https://staging.apimac.momentumamp.com';

export class FusionNowNotSubscribedError extends Error {
  constructor() {
    super('Not a MAC subscriber');
    this.name = 'FusionNowNotSubscribedError';
  }
}

let _cachedToken: string | null = null;
let _tokenExpiry: number | null = null;

export function clearFusionNowTokenCache(): void {
  _cachedToken = null;
  _tokenExpiry = null;
}

export async function getFusionNowToken(): Promise<string> {
  if (_cachedToken && _tokenExpiry && Date.now() < _tokenExpiry - 60_000) {
    return _cachedToken;
  }

  const agentId = localStorage.getItem('NowcertsAgentId');
  if (!agentId) throw new Error('NowcertsAgentId not found in localStorage');

  const res = await fetch(
    `${FUSIONNOW_BASE_URL}/api/auth/agent_token.json?agent_id=${encodeURIComponent(agentId)}`
  );
  if (res.status === 404) throw new FusionNowNotSubscribedError();
  if (!res.ok) throw new Error(`Token request failed: ${res.status}`);

  const data = await res.json();
  if (!data.success || !data.token)
    throw new Error('Failed to obtain FusionNow token');
  if ('subscription_info' in data && data.subscription_info !== 'active')
    throw new FusionNowNotSubscribedError();

  try {
    const payload = JSON.parse(
      atob(data.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    _tokenExpiry = payload.exp ? payload.exp * 1000 : Date.now() + 3_600_000;
  } catch {
    _tokenExpiry = Date.now() + 3_600_000;
  }

  _cachedToken = data.token;
  return _cachedToken as string;
}

// ── MAC AI Chat token ─────────────────────────────────────────────────────────
//
// auth0_config is a BE proxy — it receives our FusionNow JWT, performs the
// Auth0 client_credentials exchange server-side, and returns the final
// access_token directly. No client_secret ever touches the frontend.
//
// Confirmed BE response shape:
//   { access_token: string, expires_in: 86400, token_type: "Bearer", scope: string }
// ─────────────────────────────────────────────────────────────────────────────

export const MAC_AI_API_BASE =
  process.env.REACT_APP_MAC_AI_API_BASE ||
  'https://mac.api.fusionnowcrm.com/api/v1';

let _macAiToken: string | null = null;
let _macAiTokenExpiry: number | null = null;
// In-flight promise guard — prevents duplicate auth0_config fetches when
// multiple callers race before the first response is cached.
let _pendingMacAiToken: Promise<string> | null = null;

export function clearMacAIChatTokenCache(): void {
  _macAiToken = null;
  _macAiTokenExpiry = null;
  // Also null the in-flight ref — prevents a concurrent fetch from
  // resurrecting the cache after an explicit clear (e.g. on logout).
  _pendingMacAiToken = null;
}

export async function getMacAIChatToken(): Promise<string> {
  // Return cached token if still valid (with 60s buffer before expiry)
  if (
    _macAiToken &&
    _macAiTokenExpiry &&
    Date.now() < _macAiTokenExpiry - 60_000
  ) {
    return _macAiToken;
  }

  // Return the same in-flight promise to all concurrent callers —
  // prevents multiple simultaneous auth0_config requests.
  if (_pendingMacAiToken) return _pendingMacAiToken;

  _pendingMacAiToken = (async (): Promise<string> => {
    try {
      // Step 1: FusionNow JWT (reuses its own cache + subscription check)
      const fnToken = await getFusionNowToken();

      // Step 2: Exchange FN token for MAC AI access_token via BE proxy
      const res = await fetch(
        `${FUSIONNOW_BASE_URL}/api/open_api/v1/auth0_config`,
        { headers: { Authorization: `Bearer ${fnToken}` } }
      );
      if (!res.ok) throw new Error(`auth0_config failed: ${res.status}`);

      const { access_token, expires_in } = await res.json();
      if (!access_token)
        throw new Error('auth0_config: missing access_token in response');

      // Cache — prefer JWT exp claim for precision, fall back to expires_in (86400 = 24h)
      try {
        const payload = JSON.parse(
          atob(access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
        );
        _macAiTokenExpiry = payload.exp
          ? payload.exp * 1000
          : Date.now() + (expires_in ?? 86400) * 1000;
      } catch {
        _macAiTokenExpiry = Date.now() + (expires_in ?? 86400) * 1000;
      }

      _macAiToken = access_token;
      return _macAiToken as string;
    } finally {
      // Always clear the in-flight ref so the next call can proceed normally
      _pendingMacAiToken = null;
    }
  })();

  return _pendingMacAiToken;
}
